import React from "react";

import { Intro, Text } from "components/commons";
import useDataTable from "hooks/useDataTable";
import locale from "localization";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { getStationPayments, voidPayment, updatePayment } from "apis/payment.api";
import { payWithLOCQPayFilterState } from "./pay-with-locqpay-filter.state";
import { mapDataToList, mapFilterToRequest } from "./pay-with-locqpay.mapper";
import { payWithLOCQPayColumns } from "./pay-with-locqpay-columns";
import PayWithLOCQPayFilter from "./pay-with-locqpay-filter";
import { useContext } from "react";
import { StationContext } from "contexts";
import { useApi, useExport, useModal } from "hooks";
import { generateStationReport } from "apis/generate-report.api";
import { SuccessModal, ConfirmModal } from "components/modals";
import { SettlementStatus } from "enums";
import { useCallback } from "react";
import { useState } from "react";
import PayWithLOCQPayVoidModal from "./pay-with-locqpay-void-modal";
import PayWithLOCQPayActionsHistoryModal from "./pay-with-locqpay-actions-history-modal";
import PayWithLOCQPayReceiptGuideModal from "./pay-with-locqpay-receipt-guide-modal";
import DeclareReceiptIssuanceModal from "../payment-history-business/declare-receipt-issuance-modal";
import { formatAmount } from "utils";

const PayWithLOCQPayModule = () => {
  const voidPaymentModal = useModal();
  const actionsHistoryModal = useModal();
  const confirmModal = useModal();
  const receiptGuideModal = useModal();
  const declareReceiptIssuanceModal = useModal();

  const { station } = useContext(StationContext);
  const [remarks, setRemarks] = useState("");
  const [value, setValue] = useState("");

  const { request: updatePaymentRequest, loading: updatingPayment } = useApi({
    api: updatePayment,
    pageError: false,
    modalError: true,
  });

  const { request: voidRequest, loading: voidingRequest } = useApi({
    api: voidPayment,
    pageError: false,
    modalError: true,
  });

  const handleVoidPayment = ({
    paymentId,
    finalAmount,
    settlementId,
    settlementStatus,
    businessName,
    totalDiscount,
    amount,
  }) => {
    setRemarks("");
    voidPaymentModal.show({
      title: `${locale.voidTransaction}?`,
      description: (
        <Text align="center">
          <locale.Populate
            text={
              settlementId
                ? settlementStatus === SettlementStatus.ForProcessing
                  ? locale.voidingThisTransactionSettlementProcessingLOCQPay
                  : locale.voidingThisTransactionSettlementProcessedLOCPay
                : locale.voidingThisTransactionLOCQPay
            }
            items={[<b>{formatAmount(amount)}</b>, <b>{formatAmount(totalDiscount)}</b>]}
          />
        </Text>
      ),
      label: locale.reasonRemarks,
      actionText: locale.voidTransaction,
      submit: (value) => {
        onVoidPaymentCb({
          paymentId,
          value,
          businessName,
          finalAmount,
        });
      },
    });
  };

  const handleDeclareReceiptIssuance = (payments) => {
    declareReceiptIssuanceModal.show({
      redemptionItem: payments,
    });
  };

  const handleReceiptGuide = (payments) => {
    receiptGuideModal.show({
      redemptionItem: payments,
      title: `${locale.receiptGuide}`,
      description: "",
      label: `${locale.reason}/${locale.remarks}`,
    });
  };

  const handleActionsHistory = ({ voidedByEmail, status, voidedAt }) => {
    actionsHistoryModal.show({
      title: locale.actionsHistory,
      voidedByEmail,
      status,
      voidedAt,
    });
  };

  const onVoidPaymentCb = useCallback(
    async ({ paymentId, value, businessName, finalAmount }) => {
      await voidRequest({ paymentId, remarks: value });
      voidPaymentModal.close();
      table.fetch({});

      confirmModal.show({
        title: locale.transactionVoided,
        content: (
          <locale.Populate
            text={locale.thisTransactionHasBeenVoided}
            items={[<b>{formatAmount(finalAmount)}</b>, <b>{businessName}</b>]}
          />
        ),
        loading: voidingRequest,
        primary: {
          text: locale.gotIt,
          onClick: () => {
            confirmModal.close();
          },
        },
      });
      setRemarks("");
    },
    //eslint-disable-next-line
    [voidRequest, voidPaymentModal, confirmModal, voidingRequest]
  );

  const { filter, search, table } = useDataTable({
    api: { api: getStationPayments, params: { stationId: station.stationId, platformType: "plb" } },
    filter: { initialState: payWithLOCQPayFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "payments",
      mapper: (payments) =>
        mapDataToList({ payments, handleVoidPayment, handleReceiptGuide, handleActionsHistory, handleDeclareReceiptIssuance }),
      columns: payWithLOCQPayColumns,
    },
  });

  const exports = useExport({
    api: generateStationReport,
    hasModal: true,
    reportType: "fuel-card",
    mappedFilterState: {
      ...filter?.mappedFilterState,
      searchKey: search?.searchKey,
      stationIds: station.stationId.toString(),
      redemptionStationId: station.stationId,
    },
  });

  return (
    <>
      <SuccessModal {...exports?.successModalComponent} />
      <ConfirmModal {...confirmModal} />
      <div>
        <Intro title={locale.populate(locale.paymentHistoryTitle, [locale.plbLocqpay])}subtitle={locale.viewPayWithLOCQPayTransactions} />

      </div>
      <PayWithLOCQPayFilter {...filter} {...search} {...exports} />
      <DataTableV2 {...table} />
      <PayWithLOCQPayActionsHistoryModal {...actionsHistoryModal} />
      <PayWithLOCQPayVoidModal
        {...voidPaymentModal}
        remarks={remarks}
        onChangeRemarks={(v) => {
          setRemarks(v);
        }}
        loading={voidingRequest}
        onSubmit={voidPaymentModal.submit}
        value={value}
        setValue={setValue}
      />

      <PayWithLOCQPayReceiptGuideModal
        {...receiptGuideModal}
        remarks={remarks}
        value={value}
        setValue={setValue}
      />
      <DeclareReceiptIssuanceModal
        {...declareReceiptIssuanceModal}
        loading={updatingPayment}
        onConfirm={async ({ orNumber }) => {
          await updatePaymentRequest({
            paymentId: declareReceiptIssuanceModal.redemptionItem?.paymentId,
            body: { orNumber },
          });
          declareReceiptIssuanceModal.close();
          table.fetch({});
        }}
      />
    </>
  );
};

export default PayWithLOCQPayModule;
